import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import LoginForm, { Props as LoginFormProps } from './LoginForm/LoginForm';
import Button from 'components/UI/Buttons/Button/Button';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import { getRoutesUri } from 'services/generic';
import { ENV_NEWSLETTER_HIGHTLIGHTING_SIGNUP } from 'settings/env';

import styles from './Login.module.scss';

type Props = {
  pageType: 'account' | 'checkout';
  classes?: Partial<{
    container: string;
    login: {
      container?: string;
      form?: LoginFormProps['classes'];
      requiredFieldsInfo?: string;
    };
    newCustomer: {
      container?: string;
    };
  }>;
};

export default function Login({ pageType, classes }: Props): JSX.Element {
  const { t } = useTranslation(['loginPage']);
  const loginFormClasses = useMemo(() => {
    const { container, ...others } = classes?.login?.form || {};

    return {
      container: classNames(styles.formContainer, container),
      ...others,
    };
  }, [classes?.login?.form]);

  return (
    <div className={classNames(styles.container, classes?.container)}>
      <div
        className={classNames(styles.customerLogin, classes?.login?.container)}
      >
        <h2 className={styles.blockTitle}>{t('LOGIN_ALREADY_CUSTOMER')}</h2>
        <LoginForm
          isCheckoutPage={pageType === 'checkout'}
          classes={loginFormClasses}
        />
        <RequiredFields
          classes={{
            wrapper: classNames(
              styles.requiredFieldsInfo,
              classes?.login?.requiredFieldsInfo
            ),
            asterisk: styles.asterisk,
          }}
        />
      </div>
      <div
        className={classNames(
          styles.newCustomer,
          classes?.newCustomer?.container
        )}
      >
        <h2 className={styles.blockTitle}>{t('LOGIN_NEW_CUSTOMER')}</h2>
        {ENV_NEWSLETTER_HIGHTLIGHTING_SIGNUP && (
          <p className={styles.newsletterMessage}>
            {t('LOGIN_NEWSLETTER_MARKETING_MESSAGE', {
              OFFERED_MESSAGE: ENV_NEWSLETTER_HIGHTLIGHTING_SIGNUP,
            })}
          </p>
        )}
        <Button
          component={Link}
          model={'primary'}
          fullWidth
          className={styles.newCustomerLink}
          to={
            pageType === 'checkout'
              ? getRoutesUri<string>('checkoutSignup')
              : getRoutesUri<string>('customerSignup')
          }
        >
          {t('LOGIN_CREATE_MY_ACCOUNT')}
        </Button>
      </div>
      <RequiredFields
        classes={{
          wrapper: styles.requiredFieldsInfo,
          asterisk: styles.asterisk,
        }}
      />
    </div>
  );
}
